import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils.js';
import { imageSrc } from '../../lib/imageSrc.js';

type Props = {
  url: string | null | undefined;
  alt: string;
  /** Pixel size of the inline thumbnail. Defaults to 44 — enough to recognize
   *  the drawing at a glance without dominating the row. */
  size?: number;
  /** Show a folder badge in the corner (assemblies). */
  assemblyBadge?: boolean;
  /** Disable the hover-zoom popover entirely (e.g. inside another tooltip). */
  noHoverZoom?: boolean;
  className?: string;
  /** Override label shown on hover popover. Defaults to alt. */
  hoverLabel?: string;
  /**
   * התמונה לא נטענה (404/401/רינדור שבור). נקרא פעם אחת לכל `url`.
   *
   * אלי, 26/09/2026, על התמונות בעצי המוצר: „אפשר לראות אותה." תמונה שבורה
   * אינה „אין שרטוט" — והמסך צריך להבדיל, ולהציע לנסות שוב. בלי המאזין הזה
   * הקורא לא יכול לדעת, כי `<img>` נכשל בשקט.
   */
  onError?: () => void;
};

const HOVER_DELAY_MS = 250;
const POPOVER_W = 360;
const POPOVER_H = 280;

/**
 * Standardized drawing thumbnail with hover-zoom. The inline image is sized
 * large enough to RECOGNIZE the drawing (default 44px) — too small and the
 * manager sees only smudges. On hover (after a short delay so flicking the
 * cursor across a list doesn't spam popovers), a portal renders a much
 * larger preview anchored next to the cursor target, with the part label.
 *
 * Uses a portal so the popover escapes overflow:hidden parents (tree row,
 * gallery card) and z-index'd into the foreground.
 */
export function Thumbnail({
  url, alt, size = 44, assemblyBadge = false, noHoverZoom = false, className, hoverLabel, onError,
}: Props): JSX.Element | null {
  const [showPopover, setShowPopover] = useState(false);
  const [popoverPos, setPopoverPos] = useState<{ top: number; left: number } | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  function onEnter(): void {
    if (noHoverZoom || !url) return;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      const el = ref.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const PAD = 12;
      // Default: open to the START side (right in LTR, left in RTL). In our
      // RTL layout that's to the LEFT of the thumb (negative dx). Clamp to
      // viewport so the popover never gets clipped.
      let left = rect.left - POPOVER_W - PAD;
      if (left < PAD) left = rect.right + PAD;
      let top = rect.top + rect.height / 2 - POPOVER_H / 2;
      if (top < PAD) top = PAD;
      if (top + POPOVER_H > window.innerHeight - PAD) {
        top = window.innerHeight - POPOVER_H - PAD;
      }
      setPopoverPos({ top, left });
      setShowPopover(true);
    }, HOVER_DELAY_MS);
  }

  function onLeave(): void {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setShowPopover(false);
  }

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  if (!url) return null;
  const src = imageSrc(url) ?? '';

  return (
    <>
      <div
        ref={ref}
        className={cn('relative flex-shrink-0', className)}
        style={{ width: size, height: size }}
        onMouseEnter={onEnter}
        onMouseLeave={onLeave}
      >
        <img
          src={src}
          alt={alt}
          // ── רק מה שנראה נטען (26/09/2026) ──────────────────────────────
          // עץ מוצר של 30+ חלקים שלח 30+ בקשות תמונה בטעינה, כולן בו-זמנית,
          // גם על שורות שהמנהל לא גלל אליהן. `loading="lazy"` הוא מה שהדפדפן
          // כבר יודע לעשות — ואין כאן ספרייה חדשה ולא IntersectionObserver.
          loading="lazy"
          decoding="async"
          onError={onError}
          // object-contain (not cover): show the WHOLE drawing sheet so the part
          // is recognizable at a glance, instead of a center-cropped smudge.
          className={cn(
            'w-full h-full rounded object-contain border bg-white',
            assemblyBadge ? 'border-amber-300 ring-1 ring-amber-100' : 'border-slate-200',
          )}
        />
        {assemblyBadge && (
          <span
            className="absolute -bottom-0.5 -end-0.5 inline-flex items-center justify-center rounded-full bg-amber-500 border border-white text-white"
            style={{ width: Math.max(12, size / 3), height: Math.max(12, size / 3) }}
          >
            <svg viewBox="0 0 24 24" className="w-2/3 h-2/3 fill-none stroke-current stroke-2">
              <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" />
            </svg>
          </span>
        )}
      </div>

      {/* Hover popover — portal so it escapes overflow constraints */}
      {showPopover && popoverPos && createPortal(
        <div
          dir="rtl"
          className="fixed z-[1000] pointer-events-none animate-in fade-in zoom-in-95 duration-150"
          style={{
            top: popoverPos.top,
            left: popoverPos.left,
            width: POPOVER_W,
          }}
        >
          <div className="bg-white border border-slate-300 rounded-xl shadow-2xl overflow-hidden">
            <div
              className="bg-slate-50 flex items-center justify-center"
              style={{ height: POPOVER_H }}
            >
              <img
                src={src}
                alt={alt}
                className="max-w-full max-h-full object-contain"
              />
            </div>
            <div className="px-3 py-2 border-t border-slate-200 bg-white">
              <div className="text-sm font-mono font-bold text-slate-900" dir="ltr">
                {alt}
              </div>
              {hoverLabel && hoverLabel !== alt && (
                <div className="text-xs text-slate-500 mt-0.5">{hoverLabel}</div>
              )}
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
